import { createContext, useContext, useState, useEffect } from 'react';
import { API_URL as API } from '../config/env.js';
import { clearPaperCache } from '../utils/paperCache.js';
import { isNativeApp } from '../utils/platform.js';
import { initPushNotifications, refreshPushRegistration } from '../utils/pushNotifications.js';
import { getEditionAlertsEnabled } from '../utils/editionAlerts.js';

const AuthContext = createContext();

/** Local dev: full subscriber experience while logged in (npm start). */
const DEV_FULL_ACCESS = import.meta.env.DEV;

export function isSubscriptionActive(user) {
  if (!user) return false;
  if (DEV_FULL_ACCESS) return true;
  if (user.isAdmin || user.isOwner || user.isPreview) return true;
  if (user.trialUntil && Number(user.trialUntil) > Date.now()) return true;
  return !!(user.isSubscribed && Number(user.subscribedUntil) > Date.now());
}

export function hasFullAccess(user) {
  return isSubscriptionActive(user);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('rk_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const login = (userData) => {
    localStorage.setItem('rk_user', JSON.stringify(userData));
    setUser(userData);
    if (isNativeApp() && userData?.token && getEditionAlertsEnabled()) {
      void refreshPushRegistration(userData.token);
    }
  };

  const logout = () => {
    localStorage.removeItem('rk_user');
    setUser(null);
    clearPaperCache();
    if (isNativeApp()) window.location.replace('/login');
  };

  const updateSubscription = (isSubscribed, subscribedUntil) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, isSubscribed, subscribedUntil };
      localStorage.setItem('rk_user', JSON.stringify(updated));
      return updated;
    });
    if (isSubscribed) clearPaperCache();
  };

  const syncUser = (patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const wasLocked = !isSubscriptionActive(prev);
      const updated = { ...prev, ...patch };
      localStorage.setItem('rk_user', JSON.stringify(updated));
      if (wasLocked && isSubscriptionActive(updated)) clearPaperCache();
      return updated;
    });
  };

  // Keep access in sync with server (fixes paid users stuck with stale localStorage).
  useEffect(() => {
    if (!user?.token) return undefined;
    let cancelled = false;

    async function refreshStatus() {
      try {
        const res = await fetch(`${API}/api/auth/status`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        setUser((prev) => {
          if (!prev) return prev;
          const wasLocked = !isSubscriptionActive(prev);
          const updated = {
            ...prev,
            isSubscribed: data.isSubscribed,
            isTrial: data.isTrial,
            isAdmin: data.isAdmin,
            isOwner: data.isOwner,
            isPreview: data.isPreview,
            subscribedUntil: data.subscribedUntil,
            trialUntil: data.trialUntil,
          };
          localStorage.setItem('rk_user', JSON.stringify(updated));
          if (wasLocked && isSubscriptionActive(updated)) clearPaperCache();
          return updated;
        });
      } catch {
        /* ignore */
      }
    }

    refreshStatus();

    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user?.token]);

  useEffect(() => {
    if (!isNativeApp() || !getEditionAlertsEnabled()) return;
    if (user?.token) void refreshPushRegistration(user.token);
    else void initPushNotifications();
  }, [user?.token]);

  return (
    <AuthContext.Provider value={{ user, login, logout, updateSubscription, syncUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
